/**
 * `Responsável` (Figma `325:12257`, popover `638:18874`): one tracked account, or a name with
 * no account behind it -- never several accounts, and never a name plus an account.
 *
 * The account list is already filtered server-side by `q`; cmdk's own client-side filter
 * otherwise hides every item whose `value` (the account id) does not itself contain the typed
 * text (`AccountLink` carried the same note).
 */
import { useRef, useState, type ReactNode } from 'react';
import { Plus, Search, X } from 'lucide-react';

import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { EntityRow } from '../../../chrome/entity/EntitySwitcher';
import { HintedIcon, IconHint } from '../../../chrome/hint';
import { initials } from '../../../chrome/shell/initials';
import { useTrackedAccounts } from '../accounts/api/queries';
import type { TrackedAccountRow } from '../accounts/api/schemas';
import { accountOption, platformLabel, PlatformIcon } from '../accounts/NoSelection';
import { t } from '../i18n';

export type LeadSelection =
  { kind: 'account'; account: TrackedAccountRow } | { kind: 'name'; name: string };

export interface LeadPickerProps {
  campaignId: string;
  value: LeadSelection | null;
  onChange: (value: LeadSelection | null) => void;
}

function labelOf(selection: LeadSelection): string {
  if (selection.kind === 'name') return selection.name;
  return selection.account.label ?? selection.account.handle ?? selection.account.id;
}

export function LeadPicker(props: LeadPickerProps): ReactNode {
  const { campaignId, value, onChange } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const accountsQuery = useTrackedAccounts(campaignId, { q: query });
  const trimmedQuery = query.trim();

  if (value !== null) {
    const label = labelOf(value);
    return (
      <div className="flex h-9 w-full items-center rounded-md border border-input bg-card px-2">
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-secondary py-0.5 pr-1.5 pl-0.5">
          <Avatar size="sm">
            <AvatarFallback>{initials(label)}</AvatarFallback>
          </Avatar>
          <span className="text-body-sm font-medium text-foreground">{label}</span>
          {value.kind === 'account' && (
            <>
              <HintedIcon label={platformLabel(value.account.platform)}>
                <PlatformIcon platform={value.account.platform} className="size-3" />
              </HintedIcon>
              {/* Only when the handle carries information the name above does not already show
               *  -- an account with no `label` already displays its handle as the name. */}
              {(value.account.handle ?? value.account.id) !== label && (
                <span className="text-caption text-muted-foreground">
                  {value.account.handle ?? value.account.id}
                </span>
              )}
            </>
          )}
          <IconHint label={t('teams.newTeam.lead.remove', { name: label })}>
            <button type="button" onClick={() => onChange(null)}>
              <X aria-hidden className="size-3" />
            </button>
          </IconHint>
        </span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* tailwind-merge does not know this project's font-size tokens: beside a text colour it
         *  drops `text-body-sm` outright, and without one it keeps `Button`'s base `text-sm`,
         *  which wins on stylesheet order. `!` survives both and lets the token's 1.5
         *  line-height through. */}
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          aria-haspopup="listbox"
          aria-label={t('teams.newTeam.lead.label')}
          className="w-full justify-start border-input bg-card font-normal text-body-sm! text-muted-foreground"
        >
          <Search aria-hidden className="size-3.5 shrink-0" />
          {t('teams.newTeam.lead.placeholder')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {/* `CommandInput`'s `aria-controls` names `CommandList`'s id unconditionally (cmdk), so
         *  the list stays mounted even with nothing to show. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('teams.newTeam.lead.searchPlaceholder')}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandGroup heading={t('teams.newTeam.lead.group')}>
              {accountsQuery.isSuccess && accountsQuery.data.length === 0 && (
                <CommandItem value="__empty__" disabled>
                  {t('teams.newTeam.lead.empty')}
                </CommandItem>
              )}
              {(accountsQuery.data ?? []).map((account) => (
                <CommandItem
                  key={account.id}
                  value={account.id}
                  onSelect={() => {
                    onChange({ kind: 'account', account });
                    setOpen(false);
                    setQuery('');
                    triggerRef.current?.focus();
                  }}
                >
                  <EntityRow option={accountOption(account)} />
                </CommandItem>
              ))}
            </CommandGroup>
            {trimmedQuery !== '' && (
              <CommandGroup>
                <CommandItem
                  value={`__use-name__${trimmedQuery}`}
                  onSelect={() => {
                    onChange({ kind: 'name', name: trimmedQuery });
                    setOpen(false);
                    setQuery('');
                    triggerRef.current?.focus();
                  }}
                >
                  <Plus aria-hidden className="size-3.5" />
                  {t('teams.newTeam.lead.useNameOnly', { name: trimmedQuery })}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
